import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight, PlayCircle } from "lucide-react";
import { type GalleryImage } from "@/types/memorial";
import { useIsMobile } from "@/hooks/use-mobile";
import { getThumbnail, getDefaultThumbnail } from "../media/utils/thumbnailExtractor";
import { supabase } from "@/integrations/supabase/client";

interface GalleryItemProps {
  image: GalleryImage;
  allImages: GalleryImage[];
}

export const GalleryItem = React.memo(({ image, allImages }: GalleryItemProps) => {
  const isVideo = (img: GalleryImage) => {
    return img.stream_id ||
           img.src.toLowerCase().endsWith('.mp4') ||
           img.src.toLowerCase().endsWith('.mov') ||
           img.src.toLowerCase().endsWith('.avi');
  };
  
  const isMobile = useIsMobile();
  const [posterImage, setPosterImage] = useState<string>("/video-placeholder.jpg");
  const [thumbnailError, setThumbnailError] = useState(false);
  
  // Find the index of the current image in the allImages array
  const initialSlideIndex = allImages.findIndex(img => img.id === image.id);
  const [api, setApi] = useState<CarouselApi>(undefined);
  
  // Get public URL from Supabase storage path
  const getPublicUrl = (path: string): string => {
    if (!path) return "";
    
    // If it already looks like a full URL, return it
    if (path.startsWith('http')) return path;
    
    // Otherwise, get the public URL from the storage path
    try {
      const { data: { publicUrl } } = supabase.storage
        .from('memorials')
        .getPublicUrl(path);
      
      return publicUrl;
    } catch (error) {
      console.error(`[GalleryItem] Error getting public URL:`, error);
      return "";
    }
  };
  
  // When the carousel is initialized, set it to the correct slide
  const onCarouselCreated = (carouselApi: CarouselApi) => {
    setApi(carouselApi);
    if (carouselApi && initialSlideIndex >= 0) {
      // Small timeout to ensure the carousel is fully initialized
      setTimeout(() => {
        carouselApi.scrollTo(initialSlideIndex);
      }, 50);
    }
  };

  useEffect(() => {
    if (!isVideo(image)) return;

    // Use provided thumbnail if available
    if (image.thumbnail && !thumbnailError) {
      const fullThumbnailUrl = getPublicUrl(image.thumbnail);
      setPosterImage(fullThumbnailUrl);
      return;
    }

    // Try to get thumbnail from localStorage
    const cachedThumbnail = getThumbnail(image.src);
    if (cachedThumbnail && !thumbnailError) {
      setPosterImage(cachedThumbnail);
      return;
    }

    // Fallback to default thumbnail
    const defaultThumb = getDefaultThumbnail(image.src);
    setPosterImage(defaultThumb);
  }, [image.src, image.thumbnail, thumbnailError]);

  const handleThumbnailError = () => {
    console.error(`[GalleryItem] Error loading thumbnail:`, image.thumbnail);
    setThumbnailError(true);
    setPosterImage(getDefaultThumbnail(image.src));
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="aspect-square w-full hover:opacity-90 transition-opacity relative">
          {isVideo(image) ? (
            <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
              <img 
                src={posterImage}
                alt="Video thumbnail" 
                className="w-full h-full object-cover opacity-70"
                onError={handleThumbnailError}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <PlayCircle className={`${isMobile ? "w-12 h-12" : "w-16 h-16"} text-white opacity-90`} />
              </div>
            </div>
          ) : (
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error(`[GalleryItem] Error loading image: ${image.src}`);
                e.currentTarget.src = '/placeholder.svg';
              }}
            />
          )}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl p-0 sm:p-6">
        <Carousel className="relative w-full" setApi={onCarouselCreated}>
          <CarouselContent>
            {allImages.map((carouselImage) => (
              <CarouselItem key={carouselImage.id}>
                <div className="aspect-square sm:aspect-video w-full">
                  {isVideo(carouselImage) ? (
                    carouselImage.stream_id ? (
                      <iframe
                        src={`https://iframe.videodelivery.net/${carouselImage.stream_id}`}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full border-0"
                      />
                    ) : (
                      <video
                        src={carouselImage.src}
                        controls
                        className="w-full h-full object-contain"
                        poster={carouselImage.thumbnail || getDefaultThumbnail(carouselImage.src)}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )
                  ) : (
                    <img
                      src={carouselImage.src}
                      alt={carouselImage.alt}
                      className="w-full h-full object-contain"
                      onError={(e) => e.currentTarget.src = '/placeholder.svg'}
                    />
                  )}
                  {carouselImage.caption && (
                    <p className="text-sm text-gray-600 mt-2 text-center">
                      {carouselImage.caption}
                    </p>
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="absolute left-1 sm:left-2 h-6 w-6 sm:h-10 sm:w-10">
            <ChevronLeft className="h-3 w-3 sm:h-6 sm:w-6" />
          </CarouselPrevious>
          <CarouselNext className="absolute right-1 sm:right-2 h-6 w-6 sm:h-10 sm:w-10">
            <ChevronRight className="h-3 w-3 sm:h-6 sm:w-6" />
          </CarouselNext>
        </Carousel>
      </DialogContent>
    </Dialog>
  );
});
